import { useController, useFormContext, Path } from 'react-hook-form';
import { useState } from 'react';
import { CollectionMetadata } from '../types';
import { useMetadataEditorContext } from '../context/MetadataEditorContext';

export interface UseMetadataFieldOptions<TValue> {
    name: Path<CollectionMetadata>;
    formatOption?: (val: TValue) => string;
}

export const useMetadataField = <TValue>({
    name,
    formatOption,
}: UseMetadataFieldOptions<TValue>) => {
    const { getSourceOptions } = useMetadataEditorContext();
    const { control, resetField } = useFormContext<CollectionMetadata>();
    const { field, fieldState } = useController({ name, control });

    const typedField = { ...field, value: field.value as TValue };

    const availableSources = getSourceOptions<TValue>(name).map((opt) => ({
        ...opt,
        displayValue:
            formatOption && opt.value
                ? formatOption(opt.value)
                : (opt.value as string),
    }));

    const getDerivedSource = (val: TValue) => {
        const match = availableSources.find(
            (opt) =>
                opt.key !== 'user_overrides' &&
                JSON.stringify(opt.value) === JSON.stringify(val),
        );
        return match ? match.key : 'user_overrides';
    };

    const [defaultSource] = useState(() => getDerivedSource(typedField.value));
    const [explicitSource, setExplicitSource] = useState<string | null>(null);
    const [prevValue, setPrevValue] = useState(typedField.value);

    if (typedField.value !== prevValue) {
        setPrevValue(typedField.value);

        if (explicitSource) {
            const explicitOpt = availableSources.find(
                (o) => o.key === explicitSource,
            );
            const stillMatches =
                explicitOpt &&
                JSON.stringify(explicitOpt.value) ===
                    JSON.stringify(typedField.value);

            if (!stillMatches && explicitSource !== 'user_overrides') {
                setExplicitSource(null);
            }
        }
    }

    const activeSource =
        explicitSource !== null
            ? explicitSource
            : getDerivedSource(typedField.value);

    const handleSourceChange = (sourceKey: string) => {
        setExplicitSource(sourceKey);
        if (sourceKey !== 'user_overrides') {
            const opt = availableSources.find((o) => o.key === sourceKey);
            if (opt) field.onChange(opt.value);
        }
    };

    const handleRevert = () => {
        setExplicitSource(null);
        resetField(name);
    };

    const isExplicitOverride =
        explicitSource !== null &&
        explicitSource !== getDerivedSource(typedField.value);
    const isDirty = fieldState.isDirty || isExplicitOverride;

    return {
        field: typedField,
        fieldState,
        isDirty,
        availableSources,
        activeSource,
        defaultSource,
        handleSourceChange,
        handleRevert,
    };
};

export type UseMetadataFieldReturn<TValue> = ReturnType<
    typeof useMetadataField<TValue>
>;
